// Raising a work request from a portal tool.
//
// Tools currently email their request to an intake address, where the email is the task.
// This records it as well, so the work can be tracked, grouped by firm, and shown to the
// firm that asked for it.
//
// RAISING A REQUEST MUST NEVER BREAK A SUBMISSION. The email still goes; the record is
// additional. A tool that failed to submit because the queue was unavailable would be
// strictly worse than the position this replaces, so every failure here is swallowed and
// reported, never thrown.

const ENDPOINT = "/api/portal-requests";

/**
 * Records a work request. Returns the created request, or null if it could not be
 * recorded - callers should carry on either way.
 */
export async function raisePortalRequest({
  requestType,
  title,
  // The firm the tool is running for, not whoever is signed in. An administrator working
  // inside a firm's portal is raising work for that firm, and filing it under their own
  // account would hide it from the firm that needs to see it.
  //
  // Only honoured for administrators: the API takes the firm from the session for everyone
  // else, so a firm account cannot raise work against another firm by sending this.
  brand = "",
  clientName = "",
  fileNumber = "",
  priority = "normal",
  payload = {},
  dueAt = null,
}) {
  if (!requestType || !title) return null;

  try {
    const response = await fetch(ENDPOINT, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        brand: brand || undefined,
        request_type: requestType,
        title,
        client_name: clientName || null,
        file_number: fileNumber || null,
        priority,
        payload,
        due_at: dueAt,
      }),
    });
    if (!response.ok) {
      // 404 while the table does not exist yet, 503 when it is unconfigured. Neither is
      // worth putting in front of someone who has just submitted a form successfully.
      console.warn(`Portal request not recorded (${response.status}).`);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.warn("Portal request not recorded:", error);
    return null;
  }
}

// A readable label for each kind of request, used in the queue and in the firm's view.
export const REQUEST_TYPE_LABELS = {
  apply_for_aid: "Application for aid",
  additional_prep: "Additional prep worksheet",
  travel_claim: "Travel claim",
  conflict_check: "Conflict check",
  backsheet: "Backsheet to counsel",
  intake: "Intake form",
  memo_precedent: "Memo precedent",
  authority_to_act: "Authority to act",
  vla_report: "Medical report worksheet",
  certification: "Guideline certification",
  billing: "Billing sheet",
  bundle: "Document bundle",
  // Raised by the email intake job from a tagged email (api/cron/email-intake.js).
  new_aid_request: "New aid request",
  extension_request: "Extension request",
  disbursement_request: "Disbursement request",
};

// A request's attachments are only linked if they point at the portal's own file route.
// payload is free-form JSON, so a link in it is not trusted just for being there.
export function requestAttachments(request) {
  const list = Array.isArray(request?.payload?.attachments) ? request.payload.attachments : [];
  return list.filter((attachment) => /^\/api\/files\?path=[^&]+$/.test(String(attachment?.url || "")));
}

export function requestTypeLabel(requestType) {
  return REQUEST_TYPE_LABELS[requestType] || String(requestType || "Request").replace(/_/g, " ");
}

export const REQUEST_STATUS_LABELS = {
  received: "Received",
  in_progress: "In progress",
  blocked: "Blocked",
  completed: "Completed",
  cancelled: "Cancelled",
};

// Narrow a queue to one person's own requests.
//
// Extracted rather than filtered inline so it can be tested. The matching is on email
// because that is the one field the server sets from the session - `submitted_by` is a
// display name, and two people at a firm can share one.
//
// A row with no submitter email never matches. Treating a blank as "mine" would quietly
// show one person another's work, which is the opposite of what this is for.
export function filterToSubmitter(requests, email) {
  const mine = String(email || "").trim().toLowerCase();
  if (!mine) return [];
  return (Array.isArray(requests) ? requests : []).filter((request) => {
    const submitter = String(request?.submitted_by_email || "").trim().toLowerCase();
    return submitter !== "" && submitter === mine;
  });
}

// Whose request this is now, as opposed to who first asked for it.
//
// A request belongs to the person it is assigned to. Until somebody is named, it still
// belongs to whoever raised it - that is what makes a departed lawyer's unclaimed work
// findable rather than orphaned.
export function filterToMine(requests, email) {
  const mine = String(email || "").trim().toLowerCase();
  if (!mine) return [];
  return (Array.isArray(requests) ? requests : []).filter((request) => {
    const assigned = String(request?.assigned_to_email || "").trim().toLowerCase();
    if (assigned) return assigned === mine;
    const submitter = String(request?.submitted_by_email || "").trim().toLowerCase();
    return submitter !== "" && submitter === mine;
  });
}
